<template>
    <tr>
        <td @click="editingField = 'product'" class="pl-10">
            <div v-if="editingField === 'product'">
                <div class="td-inline" v-if="type === 'pharmacy'">
                    <AsyncSelect
                        placeholder="Select Product"
                        v-model="selectedProduct"
                        label="text"
                        :api-service="fetchProductDropdownList"
                        :format-label="option => option.text"
                        :option-selected="productSelected"
                        :additional-query="additionalProductQuery"
                        :min-char-to-search="2"
                        skip-api-on-open
                        ref="productField"
                        @close="editingField = null"
                        :selectable="option => ! tableItems.some(item => item.product_id === option.id)"
                        v-focus
                    />
                </div>
                <div class="td-inline" v-else>
                    <strong>
                        <h6>{{ item.name }}</h6>
                    </strong>
                    {{ item.description }}
                </div>
            </div>
            <div v-else class="col">
                <strong>
                    <h6>{{ item.name }}</h6>
                </strong>
            </div>
        </td>

        <td @click="editingField = 'quantity'" class="text-center">
          <input
            v-if="editingField === 'quantity'"
            v-model.number="item.quantity"
            type="number"
            class="form-control width-60"
            @blur="editingField = null"
            v-focus
          >
          <span v-else>{{ item.quantity }}</span>
        </td>

        <td @click="editingField = 'rate'" class="text-center">
          <input
            v-if="editingField === 'rate'"
            v-model.number="item.rate"
            type="number"
            class="form-control width-60"
            @blur="editingField = null"
            v-focus
          >
          <span v-else>{{ item.rate }}</span>
        </td>

        <td class="text-center">{{ getSubTotal.toFixed(4) }}</td>

        <td @click="editingField = 'discount_percent'" class="text-center">
          <input
            v-if="editingField === 'discount_percent'"
            v-model.number="item.discount_percent"
            :min="0"
            :max="100"
            @input="setDiscountPrice"
            type="number"
            class="form-control width-60"
            @blur="editingField = null"
            v-focus
          >
          <span v-else>{{ item.discount_percent ?? 0 }}%</span>
        </td>

        <td class="text-center">{{ getDiscountAmount.toFixed(4) }}</td>

        <td class="text-center">{{ getAmountAfterDiscount.toFixed(4) }}</td>

        <td @click="editingField = 'vat'" class="text-center">
          <input
            v-if="editingField === 'vat'"
            v-model.number="item.vat"
            type="number"
            class="form-control width-60"
            @blur="editingField = null"
            v-focus
          >
          <span v-else>{{ item.vat }}%</span>
        </td>

        <td @click="editingField = 'vat_amount'" class="text-center">
            <input
              v-if="editingField === 'vat_amount'"
              v-model.number="props.item.vat_amount"
              type="number"
              class="form-control width-60"
              @blur="editingField = null"
              :min="0"
              @input="handleVatAmountChange"
              v-focus
            >
            <span v-else>{{ getVatAmount.toFixed(4) }}</span>
        </td>

        <td class="text-center">{{ getTotal.toFixed(4) }}</td>

        <td>
            <div>
                <button @click="deleteItem(index)" class="btn btn-outline-danger btn-sm">
                    <i class="fas fa-trash"></i>
                </button>
            </div>
        </td>
    </tr>
</template>
<script setup>
import {computed, onMounted, ref, watch} from "vue";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handlePurchase from "@/services/modules/purchase";
import handleDropdowns from "@/services/modules/dropdowns";
import {useRoute} from "vue-router";

const props = defineProps({
    item: {
        type: Object,
        default: {}
    },
    index: {
        type: Number
    },
    vats: {
        type: Array,
        default: []
    },
    type: {
        type: String,
        default: 'pharmacy'
    },
    productType: {
        type: String,
        default: null
    },
    formData:{
      type:Object,
      default:null
    },
    editingCell: {
        type: Object,
        default: () => ({ index: null, field: null })
    },
    tableItems: {
        type: Array,
        default: () => []
    }
})

const route = useRoute();
const companyQuery = `?company_id=${route.params.companyId}`;
const emit = defineEmits(['onEditCell', 'onDeleteItem']);
const selectedProduct = ref(null);
const editingField = ref(null);

const { fetchProductStock } = handlePurchase();
const { fetchProductDropdownList } = handleDropdowns();

const additionalProductQuery = ref({
  location_id: props.formData.location_id || '',
  product_type: props.productType || '',
  with_stock: 1,
});

const vFocus = {
    mounted(el) {
        el.focus();
    }
};

const deleteItem = (i) => {
    emit('onDeleteItem', i)
}

const getSubTotal = computed(() => {
    return (props.item.quantity * props.item.rate)
})

const getDiscountAmount = computed(() => {
    return props.item.discount_amount;
})

const getAmountAfterDiscount = computed(() => {
    const total = getSubTotal.value;
    return total - props.item.discount_amount;

})

const getVatAmount = computed(() => {
    return props.item.vat_amount;
})

const getTotal = computed(() => {
    const total = getSubTotal.value - getDiscountAmount.value + getVatAmount.value;
    props.item.sub_total = getSubTotal.value;
    props.item.discount_amount = getDiscountAmount.value;
    props.item.vat_amount = getVatAmount.value;
    props.item.total = total;
    return total;
})

const setDiscountPrice = (e) => {
    let discountPercent = e.target.value;
    let amount = (getSubTotal.value * discountPercent) / 100;
    props.item.discount_amount = parseFloat(amount.toFixed(4))
}

const setVatAmount = (e) => {
    let vatPercent = e.target.value;
    let amount = (getAmountAfterDiscount.value * vatPercent) / 100;
    props.item.vat_amount = parseFloat(amount.toFixed(4))
}

watch(
    () => [props.item.vat, getAmountAfterDiscount.value, props.formData.vat_type],
    ([vat, afterDiscount]) => {
      if (vat !== null && afterDiscount !== null) {
        let amount = (afterDiscount * vat) / 100;

        if (props.formData.vat_type == 'vat_inclusive'){
          amount = afterDiscount / (100 + vat) * vat;
        }

        props.item.vat_amount = parseFloat(amount.toFixed(4));
      }
    }
);

const handleVatAmountChange = (e) => {
  const vatAmount = parseFloat(e.target.value || 0);
  const baseAmount = getAmountAfterDiscount.value;

  props.item.vat_amount = vatAmount;

  if (!isNaN(baseAmount) && baseAmount > 0) {
    props.item.vat = parseFloat(((vatAmount / baseAmount) * 100).toFixed(4));
  }
  if (!isNaN(baseAmount) && baseAmount > 0 && props.formData.vat_type == 'vat_inclusive') {
    props.item.vat = parseFloat(((vatAmount / (baseAmount - vatAmount)) * 100).toFixed(4));
  }
};

const productSelected = () => {
    const selectedProductDesc = selectedProduct.value ? selectedProduct.value.description : null;
    
    if (selectedProductDesc) {
        props.item.rate = selectedProductDesc.sales_price;
        props.item.description = selectedProductDesc.unit ? selectedProductDesc.unit.description : null;
        return;
    }

    props.item.rate = 0;
}

watch(selectedProduct, (newValue) => {
    if (newValue) {
        props.item.product_id = newValue.id;
        props.item.name = newValue.text;
    }
});

watch(() => props.formData.location_id, (newVal) => {
  additionalProductQuery.value.location_id = newVal;
});

watch(() => props.item, (newVal) => {
    if(newVal) {
        selectedProduct.value = {
            id: props.item.product_id,
            text: props.item.name,
        };
    }
});

onMounted(() => {

    if (props.productType) {
        additionalProductQuery.value = {
            product_type: props.productType
        }
    }

    if (props.item.product_id) {
        selectedProduct.value = {
            id: props.item.product_id,
            text: props.item.name,
        };
    }
})

</script>

<style scoped>
.width-60 {
    width: 60px;
}
</style>